import React, { useState } from 'react';
import { Shield, X, AlertCircle, HelpCircle } from 'lucide-react';
import { toast } from 'react-toastify';

const TwoFactorVerification = ({ onVerify, onCancel, loading = false, showTrustDevice = true }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);

  const handleSubmit = e => {
    e.preventDefault();

    if (useBackupCode) {
      if (!backupCode.trim()) {
        toast.error('Please enter a backup code');
        return;
      }
      onVerify({ backupCode: backupCode.trim(), trustDevice });
    } else {
      if (!verificationCode || verificationCode.length !== 6) {
        toast.error('Please enter a valid 6-digit code');
        return;
      }
      onVerify({ twoFactorToken: verificationCode, trustDevice });
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="bg-blue-100 rounded-full p-2 mr-3">
              <Shield className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Two-Factor Authentication</h3>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {!useBackupCode ? (
            // Regular 2FA Code Input
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter the 6-digit code from your authenticator app:
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center text-lg font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={6}
                autoFocus
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-2">
                Open your authenticator app and enter the current code
              </p>
            </div>
          ) : (
            // Backup Code Input
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter a backup code:
              </label>
              <input
                type="text"
                value={backupCode}
                onChange={e => setBackupCode(e.target.value.toUpperCase())}
                placeholder="XXXXXXXX"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-center text-lg font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                disabled={loading}
              />
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                <div className="flex items-start">
                  <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2" />
                  <p className="text-xs text-yellow-700">
                    Each backup code can only be used once. Make sure to keep your remaining codes
                    safe.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Trust Device Option */}
          {showTrustDevice && (
            <div className="flex items-start">
              <input
                type="checkbox"
                id="trustDevice"
                checked={trustDevice}
                onChange={e => setTrustDevice(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                disabled={loading}
              />
              <label htmlFor="trustDevice" className="ml-2 block text-sm text-gray-700">
                Trust this device for 30 days
                <span className="block text-xs text-gray-500 mt-1">
                  You won't need to enter 2FA codes on this device for 30 days
                </span>
              </label>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              type="submit"
              disabled={
                loading ||
                (!useBackupCode && verificationCode.length !== 6) ||
                (useBackupCode && !backupCode.trim())
              }
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>

            <button
              type="button"
              onClick={() => setUseBackupCode(!useBackupCode)}
              className="w-full text-sm text-blue-600 hover:text-blue-800 transition-colors duration-200"
              disabled={loading}
            >
              {useBackupCode ? 'Use authenticator code instead' : 'Use backup code instead'}
            </button>

            <button
              type="button"
              onClick={onCancel}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>

        {/* Help Text */}
        <div className="mt-6 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-start">
            <HelpCircle className="h-4 w-4 text-gray-500 mt-0.5 mr-2" />
            <div className="text-xs text-gray-600">
              <p className="font-medium mb-1">Need help?</p>
              <p>
                If you've lost access to your authenticator app, use one of your backup codes. If
                you don't have backup codes, contact support for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerification;
